import { useState, useMemo } from "react";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { formatCurrency } from "@/lib/format";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

function Field({ label, value, onChange, min, max, step, prefix }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-2 min-[380px]:flex-row min-[380px]:items-center min-[380px]:justify-between">
        <Label className="text-xs">{label}</Label>
        <div className="flex items-center gap-1 rounded-md border border-[var(--glass-border)] bg-white px-2 py-1">
          {prefix && <span className="text-xs text-[var(--ink-muted)]">{prefix}</span>}
          <input inputMode="decimal" className="w-24 bg-transparent text-right text-base font-medium outline-none sm:text-sm" value={value}
            onChange={(e) => onChange(Number(e.target.value.replace(/[^0-9.]/g, "")) || 0)} />
        </div>
      </div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} />
    </div>
  );
}

function ResultCard({ rows, chart, testid }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-3">
        {rows.map((r) => (
          <div key={r.label} className="rounded-xl bg-[var(--surface-2)] p-3 text-center">
            <div className="text-[10px] uppercase text-[var(--ink-muted)]">{r.label}</div>
            <div className={`mt-1 font-heading text-lg ${r.highlight ? "text-[var(--cta)]" : "text-[var(--brand)]"}`} data-testid={r.testid}>{r.value}</div>
          </div>
        ))}
      </div>
      {chart?.length > 1 && (
        <div className="h-48" data-testid={testid}>
          <ResponsiveContainer width="100%" height={192} minWidth={0}>
            <AreaChart data={chart}>
              <defs>
                <linearGradient id="calcGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0a9970" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#0a9970" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="year" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} width={70} tickFormatter={(v) => `${(v / 100000).toFixed(0)}L`} />
              <Tooltip formatter={(v, n) => [formatCurrency(v), n === "value" ? "Value" : "Invested"]} />
              <Area type="monotone" dataKey="invested" stroke="#9ca3af" strokeWidth={1.4} strokeDasharray="4 3" fill="none" dot={false} />
              <Area type="monotone" dataKey="value" stroke="#0a9970" strokeWidth={2} fill="url(#calcGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function SipCalc({ stepUp }) {
  const [monthly, setMonthly] = useState(10000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(15);
  const [step, setStep] = useState(10);

  const { chart, invested, value } = useMemo(() => {
    const rm = rate / 100 / 12;
    let bal = 0, inv = 0, m = monthly;
    const chart = [{ year: 0, invested: 0, value: 0 }];
    for (let y = 1; y <= years; y++) {
      for (let i = 0; i < 12; i++) { bal = bal * (1 + rm) + m; inv += m; }
      chart.push({ year: y, invested: Math.round(inv), value: Math.round(bal) });
      if (stepUp) m = Math.round(m * (1 + step / 100));
    }
    return { chart, invested: inv, value: bal };
  }, [monthly, rate, years, step, stepUp]);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-5">
        <Field label="Monthly investment" value={monthly} onChange={setMonthly} min={500} max={200000} step={500} prefix="₹" />
        {stepUp && <Field label="Annual step-up" value={step} onChange={setStep} min={0} max={25} step={1} prefix="%" />}
        <Field label="Expected return (p.a.)" value={rate} onChange={setRate} min={4} max={24} step={0.5} prefix="%" />
        <Field label="Duration (years)" value={years} onChange={setYears} min={1} max={40} step={1} />
      </div>
      <ResultCard testid={stepUp ? "stepup-chart" : "sip-chart"} chart={chart} rows={[
        { label: "Invested", value: formatCurrency(invested), testid: "calc-invested" },
        { label: "Est. Gains", value: formatCurrency(value - invested), testid: "calc-gains" },
        { label: "Total Value", value: formatCurrency(value), highlight: true, testid: "calc-total" },
      ]} />
    </div>
  );
}

function LumpsumCalc() {
  const [amount, setAmount] = useState(500000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);
  const { chart, value } = useMemo(() => {
    const chart = [];
    for (let y = 0; y <= years; y++) chart.push({ year: y, invested: amount, value: Math.round(amount * Math.pow(1 + rate / 100, y)) });
    return { chart, value: chart[chart.length - 1].value };
  }, [amount, rate, years]);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-5">
        <Field label="One-time investment" value={amount} onChange={setAmount} min={5000} max={10000000} step={5000} prefix="₹" />
        <Field label="Expected return (p.a.)" value={rate} onChange={setRate} min={4} max={24} step={0.5} prefix="%" />
        <Field label="Duration (years)" value={years} onChange={setYears} min={1} max={40} step={1} />
      </div>
      <ResultCard testid="lumpsum-chart" chart={chart} rows={[
        { label: "Invested", value: formatCurrency(amount), testid: "calc-invested" },
        { label: "Est. Gains", value: formatCurrency(value - amount), testid: "calc-gains" },
        { label: "Total Value", value: formatCurrency(value), highlight: true, testid: "calc-total" },
      ]} />
    </div>
  );
}

function SwpCalc() {
  const [corpus, setCorpus] = useState(2000000);
  const [withdrawal, setWithdrawal] = useState(15000);
  const [rate, setRate] = useState(8);
  const [years, setYears] = useState(20);
  const { chart, finalBal, lastsYears } = useMemo(() => {
    const rm = rate / 100 / 12;
    let bal = corpus, lasts = null;
    const chart = [{ year: 0, value: corpus, invested: corpus }];
    for (let y = 1; y <= years; y++) {
      for (let i = 0; i < 12; i++) {
        bal = bal * (1 + rm) - withdrawal;
        if (bal <= 0 && lasts === null) lasts = y;
      }
      chart.push({ year: y, value: Math.max(0, Math.round(bal)), invested: corpus });
      if (bal <= 0) break;
    }
    return { chart, finalBal: Math.max(0, bal), lastsYears: lasts };
  }, [corpus, withdrawal, rate, years]);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-5">
        <Field label="Starting corpus" value={corpus} onChange={setCorpus} min={100000} max={50000000} step={100000} prefix="₹" />
        <Field label="Monthly withdrawal" value={withdrawal} onChange={setWithdrawal} min={1000} max={500000} step={1000} prefix="₹" />
        <Field label="Expected return (p.a.)" value={rate} onChange={setRate} min={4} max={15} step={0.5} prefix="%" />
        <Field label="Duration (years)" value={years} onChange={setYears} min={1} max={40} step={1} />
      </div>
      <ResultCard testid="swp-chart" chart={chart} rows={[
        { label: "Total Withdrawn", value: formatCurrency(Math.min(years, lastsYears || years) * 12 * withdrawal), testid: "calc-invested" },
        { label: lastsYears ? "Corpus Lasts" : "Final Corpus", value: lastsYears ? `${lastsYears} yrs` : formatCurrency(finalBal), highlight: true, testid: "calc-total" },
        { label: "Monthly Income", value: formatCurrency(withdrawal), testid: "calc-gains" },
      ]} />
    </div>
  );
}

export default function Tools() {
  return (
    <ClientLayout>
      <div className="mb-6">
        <h1 className="font-heading text-3xl text-[var(--brand)]">Calculators</h1>
        <p className="mt-1 text-sm text-[var(--ink-muted)]">Plan before you invest — SIP, step-up, lumpsum and retirement income.</p>
      </div>
      <Card className="glass-card max-w-4xl border-0">
        <CardContent>
          <Tabs defaultValue="sip">
            <TabsList className="mb-6">
              <TabsTrigger value="sip" data-testid="calc-tab-sip">SIP</TabsTrigger>
              <TabsTrigger value="stepup" data-testid="calc-tab-stepup">Step-up SIP</TabsTrigger>
              <TabsTrigger value="lumpsum" data-testid="calc-tab-lumpsum">Lumpsum</TabsTrigger>
              <TabsTrigger value="swp" data-testid="calc-tab-swp">SWP</TabsTrigger>
            </TabsList>
            <TabsContent value="sip"><SipCalc stepUp={false} /></TabsContent>
            <TabsContent value="stepup"><SipCalc stepUp={true} /></TabsContent>
            <TabsContent value="lumpsum"><LumpsumCalc /></TabsContent>
            <TabsContent value="swp"><SwpCalc /></TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </ClientLayout>
  );
}
